import { useEffect, useRef } from 'react';

import { IconRefresh } from '@tabler/icons-react';
import { motion } from 'motion/react';

import { Focusable, Tooltip } from '@cfa/react-core';

import { useRequiredSmartSearchChatContext } from '../SmartSearchChatContext';

import Loader from './Loader';
import { AssistantMessage, UserMessage } from './messages';

import { PathwayLogo } from '@/icons';

const Conversation: React.FC = () => {
  const { messages, status, error, regenerate } =
    useRequiredSmartSearchChatContext();

  const latestUserMsgRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const lastMessage = messages[messages.length - 1];
    if (messages.length > 1 && lastMessage?.role === 'user') {
      latestUserMsgRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  }, [messages]);

  const lastUserIdx = messages.findLastIndex(m => m.role === 'user');
  const showLoader =
    status === 'submitted' ||
    (status === 'streaming' && messages[messages.length - 1]?.role === 'user');
  const isStreaming = status === 'streaming';

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 0.2, duration: 0.4 }}
      className="mt-header-height flex-1 overflow-y-auto"
    >
      <div className="mx-auto flex w-full max-w-3xl flex-col gap-1 pt-6 pb-20">
        {messages.map((message, index) => {
          const ref = index === lastUserIdx ? latestUserMsgRef : undefined;
          if (message.role === 'user') {
            return (
              <div key={message.id} ref={ref}>
                <UserMessage message={message} />
              </div>
            );
          }
          if (message.role === 'assistant') {
            return <AssistantMessage key={message.id} message={message} />;
          }
          return null;
        })}
        {error && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="bg-feedback-error-base/10 border-feedback-error-base text-feedback-error-base mt-2 mb-4 flex items-center justify-between rounded-lg border px-3 py-2"
          >
            <span className="text-variant-caption1">
              Something went wrong. Please try again.
            </span>
            <button
              type="button"
              onClick={() => regenerate()}
              className="text-variant-caption1 hover:bg-feedback-error-base/20 flex cursor-pointer items-center gap-1 rounded px-2! py-1! transition-colors"
            >
              <IconRefresh size={16} />
              Retry
            </button>
          </motion.div>
        )}
        {showLoader ? (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="mt-2"
          >
            <Loader />
          </motion.div>
        ) : !error && !isStreaming ? (
          <Tooltip.Root delay={100} closeDelay={50}>
            <Focusable>
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, ease: 'easeInOut' }}
                className="mt-2 w-fit"
              >
                <PathwayLogo className="size-7" />
              </motion.div>
            </Focusable>
            <Tooltip.Content
              placement="right"
              className="text-variant-caption1! p-1.5!"
            >
              Hi there! How can I help you today?
            </Tooltip.Content>
          </Tooltip.Root>
        ) : null}
      </div>
      <div className="min-h-[62vh]" />
    </motion.div>
  );
};

export default Conversation;
